import { DateString } from 'common/UrlDate';
import { isNull } from 'lodash';
import React from 'react';
import {
  DishFirstExploreSegmentShefFragment,
  DishFirstFoodItemsSegmentFragment,
} from 'src/gqlReactTypings.generated.d';
import { ChipContainer } from 'src/shared/design-system/DishFirstCard/styles';
import {
  CuisineListing,
  NumDishesOrDeliveryFeeText,
  NumDishesOrDeliveryPriceContainer,
  ShefDetailsContainer,
  ShefName,
  TagContainer,
} from 'src/shared/design-system/DishFirstShefCard/styles';
import { DynamicImage } from 'src/shared/design-system/DynamicImage';
import { CalendarCheck } from 'src/shared/design-system/Icon/CalendarCheck';
import styled from 'styled-components';
import { BorderRadius, Colors } from 'web-common/src/shared/styles';

import { formatCurrency } from 'common/FormatUtilities';
import { match } from 'ts-pattern';
import { DishFirstCardLandscape, EnticementChip } from '../DishFirstCard';
import { getTopKCuisines } from '../DishFirstCard/utils';
import { NotAvailableFooter, NotAvailableFooterText } from '../DishFirstDishCard/styles';
import { FollowChip } from './FollowChip';
import { ShefRatingChip } from './ShefRatingChip';
import { NO_AVAILABILITY_DATE_STRING, ShefAvailabilityDateString, ShefCardAvatar } from './utils';

// Not all dish first cards on mobile are landscape, segment rows side scroll with portrait cards
const IMAGE_SIZE = 142;

const MIN_RATINGS_TO_SHOW = 3;

const FoodImage = styled(DynamicImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: ${BorderRadius.M} 0 0 ${BorderRadius.M};
`;

interface IProps {
  deliveryDate?: DateString;
  exploreSegmentShef: DishFirstExploreSegmentShefFragment;
  segment?: DishFirstFoodItemsSegmentFragment;
  onClick: () => void;
  className?: string;
  showDishCount: boolean;
  showDeliveryPrice: boolean;
  nextDeliveryDate: ShefAvailabilityDateString | null;
}

// NOTE: This should be renamed to DishCard if/when we remove the legacy explore page
export const DishFirstShefCardLandscape: React.FC<IProps> = ({
  deliveryDate,
  exploreSegmentShef,
  segment,
  onClick,
  className,
  showDishCount,
  showDeliveryPrice,
  nextDeliveryDate,
}) => {
  const { shef, dishPreviewImage, numDishes } = exploreSegmentShef;
  const { shefProfile } = shef;
  const cuisines = getTopKCuisines({ ...shefProfile });

  const imageContent = (
    <>
      <FoodImage src={dishPreviewImage} alt={shef.publicName} sourceWidth={IMAGE_SIZE} sourceHeight={IMAGE_SIZE} />
      <ChipContainer top left>
        <EnticementChip segment={segment} shef={shef} />
      </ChipContainer>
    </>
  );

  const numDishesOrPriceText = match({ showDeliveryPrice, numDishes })
    .with(
      { showDeliveryPrice: true },
      () => `${formatCurrency(shef.deliveryPrice?.finalDeliveryPrice ?? 0, true)} delivery fee`
    )
    .with({ numDishes: 1 }, () => `${numDishes} matching dish`)
    .otherwise(() => `${numDishes} matching dishes`);

  const bodyContent = (
    <div>
      <ShefDetailsContainer>
        <div>
          <ShefName>{`Shef ${shef.publicName}`}</ShefName>
          {cuisines && <CuisineListing>{cuisines}</CuisineListing>}
        </div>
        <ShefCardAvatar shef={shef} />
      </ShefDetailsContainer>
      <TagContainer>
        {shef.rating && shef.rating?.count >= MIN_RATINGS_TO_SHOW && (
          <ShefRatingChip averageRating={shef.rating.score} count={shef.rating.count} showAsFiveStar />
        )}
        {shef.shefProfile.isShefFollower && <FollowChip />}
      </TagContainer>
      {(showDishCount || showDeliveryPrice) && (
        <NumDishesOrDeliveryPriceContainer>
          <NumDishesOrDeliveryFeeText>{numDishesOrPriceText}</NumDishesOrDeliveryFeeText>
        </NumDishesOrDeliveryPriceContainer>
      )}
    </div>
  );

  const [, month, day] = nextDeliveryDate?.split('-') ?? [null, null, null];
  const { footerContent, useGrayBackground } = nextDeliveryDate
    ? {
        footerContent: (
          <NotAvailableFooter>
            <div className='mt-0.5'>
              <CalendarCheck
                color={deliveryDate === nextDeliveryDate ? Colors.KOHLRABI_DARK_10 : undefined}
                width={16}
                height={16}
              />
            </div>
            <NotAvailableFooterText color={deliveryDate === nextDeliveryDate ? Colors.KOHLRABI_DARK_10 : undefined}>
              {nextDeliveryDate === NO_AVAILABILITY_DATE_STRING ? 'Unavailable' : `Available ${month}/${day}`}
            </NotAvailableFooterText>
          </NotAvailableFooter>
        ),
        useGrayBackground: false,
      }
    : { footerContent: undefined, useGrayBackground: undefined };

  return (
    <DishFirstCardLandscape
      onClick={onClick}
      className={className}
      imageContent={imageContent}
      bodyContent={bodyContent}
      footerContent={footerContent}
      useGrayBackground={useGrayBackground}
      footerRuleEnabled={isNull(nextDeliveryDate)}
    />
  );
};
